import { formatPhoneNumber, normalizeDateFromAPI } from "../../utils/applicationFormatters";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import {
  getCoResidentInputsExcludingApplicant,
  getPropertyCoResidentInputs,
  resolvePropertyOtherIndividualsFlag,
} from "../../utils/propertyTabValidator";
import type {
  CoResidentFormRow,
  PropertyAgentRow,
  PropertyTabPropertyRecord,
} from "../../types/tabs";
import type { CoResident } from "@/types/application";
import type { ApplicationDetailData, ApplicationLayerData } from "../../types/application";
import {
  EDIT_LEASE_DURATION_MONTHS,
  PROPERTY_INLINE_FIELDS,
  PROPERTY_NOT_WORKING_ALERT_MESSAGE,
  RELATIONSHIP_OPTIONS,
  RENT_LEASE_REASONS,
} from '../../constants';

export {
  EDIT_LEASE_DURATION_MONTHS,
  PROPERTY_INLINE_FIELDS,
  PROPERTY_NOT_WORKING_ALERT_MESSAGE,
  RELATIONSHIP_OPTIONS,
  RENT_LEASE_REASONS,
};

export const trim = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/** Angular propertyinfo alert: custom/temp property or non-listed (`listed !== '1'`). */
export const shouldShowPropertyNotWorkingAlert = (params: {
  propertyIdValue: string | number | null | undefined;
  selectedPropertyData: PropertyTabPropertyRecord | null | undefined;
  property: PropertyTabPropertyRecord | null | undefined;
  applicationAllData: ApplicationDetailData | null | undefined;
}): boolean => {
  const { propertyIdValue, selectedPropertyData, property, applicationAllData } =
    params;

  const hasSelectedProperty =
    trim(propertyIdValue) !== "" || property?.property_id != null;
  if (!hasSelectedProperty) return false;

  const isCustomProperty =
    applicationAllData?.fk_temp_property_id != null ||
    (applicationAllData?.temp_property != null &&
      applicationAllData?.property == null);
  if (isCustomProperty) return true;

  const activeListedFlag = selectedPropertyData?.listed ?? property?.listed;
  if (activeListedFlag == null) return false;
  return String(activeListedFlag) !== "1";
};

export const relationshipLabel = (value: string): string =>
  RELATIONSHIP_OPTIONS.find((option) => option.value === value)?.label ?? value;

export const sanitizeLeaseDuration = (val: string | number | undefined | null): number => {
  if (val === undefined || val === null || String(val).trim() === "") {
    return EDIT_LEASE_DURATION_MONTHS;
  }
  const clean = String(val).replace(/\s*months?\s*/i, "").trim();
  const parsed = parseInt(clean, 10);
  return isNaN(parsed) ? EDIT_LEASE_DURATION_MONTHS : parsed;
};

export const normalizeRentForCompare = (value: unknown): string => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "";
  }
  const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(numeric) ? String(value).trim() : numeric.toFixed(2);
};

export const normalizeLeaseDateForCompare = (value: unknown): string => {
  if (!value) return "";
  return normalizeDateFromAPI(String(value).trim());
};

export const hasRentAmountChanged = (
  current: unknown,
  original: unknown,
): boolean =>
  normalizeRentForCompare(current) !== normalizeRentForCompare(original);

export const hasLeaseStartDateChanged = (
  current: unknown,
  original: unknown,
): boolean =>
  normalizeLeaseDateForCompare(current) !== normalizeLeaseDateForCompare(original);

/** Parse API/display dates for EditDatePicker storage (YYYY-MM-DD). */
export const parsePropertyLeaseDate = (dateValue: unknown): string => {
  if (!dateValue) return "";
  if (typeof dateValue === "string") {
    const normalized = normalizeDateFromAPI(dateValue);
    if (normalized) return normalized;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      const [month, day, year] = dateValue.split("/");
      return `${year}-${month}-${day}`;
    }
  }
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
};

export const resolveInitialPropertyState = (
  applicationAllData: ApplicationDetailData,
  property: PropertyTabPropertyRecord | null | undefined,
): string =>
  trim(
    property?.property_state ??
      applicationAllData?.property?.property_state ??
      applicationAllData?.property_state,
  );

export const resolveInitialAgentId = (
  applicationAllData: ApplicationDetailData,
): string => {
  const realId =
    applicationAllData?.agent?.user_id ??
    applicationAllData?.fk_agent_id ??
    applicationAllData?.agent?.agent_id;
  if (realId !== null && realId !== undefined && String(realId).trim() !== "") {
    return String(realId);
  }
  if (applicationAllData?.other_agent) return "Other";
  return "";
};

/** Mirrors resident Property.tsx: true if any property/lease signal exists in the data. */
export const computeHasApplied = (
  applicationAllData: ApplicationDetailData,
): boolean => {
  const candidates = [
    applicationAllData?.property_name,
    applicationAllData?.property?.property_name,
    applicationAllData?.property_address,
    applicationAllData?.property_city,
    applicationAllData?.property_state,
    applicationAllData?.property_zipcode,
  ];
  if (candidates.some((v) => trim(v) !== "")) return true;
  const rent = applicationAllData?.rent_amount;
  if (rent !== undefined && rent !== null && trim(rent) !== "") return true;
  if (applicationAllData?.property?.property_id != null) return true;
  if (applicationAllData?.fk_property_id != null) return true;
  if (applicationAllData?.fk_temp_property_id != null) return true;
  return false;
};

/** Map API co-resident records to PropertyTab form-state fields. */
export const hydrateCoResidents = (
  applicationAllData: ApplicationDetailData,
): CoResidentFormRow[] => {
  const raw = getPropertyCoResidentInputs(applicationAllData);
  return mapPropertyCoResidentInputsToRows(raw);
};

// Bare root layer (is_primary undefined) counts as primary.
export const isExplicitNonPrimaryLayer = (
  applicationData: ApplicationLayerData | null | undefined,
): boolean => {
  const flag = applicationData?.is_primary;
  if (flag === undefined || flag === null) return false;
  return !(flag === true || flag === 1 || flag === "1");
};

/** Read-only Property tab list — prefers primary-layer `co_resident`, then root, then co-applicant map. */
export const resolvePropertyCoResidentsForDisplay = (
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
): CoResidentFormRow[] => {
  // Primary layer only lists co-residents (Angular admin).
  if (isExplicitNonPrimaryLayer(applicationData)) return [];

  const layerCoResidents =
    (applicationData as ApplicationDetailData | null | undefined)?.co_resident ??
    (applicationData as ApplicationDetailData | null | undefined)?.co_residents;

  const merged: ApplicationDetailData = {
    ...(applicationAllData ?? ({} as ApplicationDetailData)),
  };

  if (
    !merged.co_resident &&
    !merged.co_residents &&
    layerCoResidents !== undefined &&
    layerCoResidents !== null
  ) {
    if (Array.isArray(layerCoResidents)) {
      merged.co_resident = layerCoResidents;
    } else if (typeof layerCoResidents === "object") {
      merged.co_resident = Object.values(layerCoResidents) as CoResident[];
    }
  }

  return hydrateCoResidents(merged);
};

/** Edit-mode co-resident state — merges application layers like Angular admin edit hydration. */
export const resolvePropertyCoResidentFormState = (
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
): { co_residents: CoResidentFormRow[]; other_individuals: boolean } => {
  const co_residents = resolvePropertyCoResidentsForDisplay(
    applicationAllData,
    applicationData,
  );
  const other_individuals = resolvePropertyOtherIndividualsFlag(
    applicationData?.other_individuals,
    applicationData,
    applicationAllData,
  );

  return { co_residents, other_individuals };
};

// Non-primary edit: read-only co-residents except viewed applicant (Angular coresidentList).
export const resolveReadOnlyCoResidents = (
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
): CoResidentFormRow[] => {
  if (!isExplicitNonPrimaryLayer(applicationData)) return [];
  return mapPropertyCoResidentInputsToRows(
    getCoResidentInputsExcludingApplicant(
      applicationAllData,
      applicationData?.applicant_id,
    ),
  );
};

/** Angular only shows Remove when saved co-applicant status is `NOT_STARTED`. */
export const canRemoveCoResident = (resident: CoResidentFormRow): boolean =>
  (resident.applicant_status ?? "NOT_STARTED") === "NOT_STARTED";

const mapPropertyCoResidentInputsToRows = (
  raw: ReturnType<typeof getPropertyCoResidentInputs>,
): CoResidentFormRow[] =>
  raw.map((r) => ({
    fk_user_id:
      r.fk_user_id !== undefined && r.fk_user_id !== null
        ? String(r.fk_user_id)
        : undefined,
    first_name: r.first_name ?? "",
    middle_name: r.middle_name ?? "",
    last_name: r.last_name ?? "",
    invite_code: r.invite_code ?? r.applicant_invite_code ?? "",
    phone: formatPhoneNumber(
      String(r.mobile_no ?? r.phone ?? "").replace(/\D/g, "").slice(0, 15),
    ),
    email: (r.email ?? "").trim().toLowerCase(),
    spouse: r.spouse === true || r.spouse === 1 || r.spouse === "1",
    relationship: r.relationship_type ?? r.relationship ?? "",
    applicant_status: r.applicant_status ?? "",
  }));

/** Map `validateInvite()` co_applicant JSON to form rows (Angular `setCoresidentData` parity). */
export const mapInviteCoApplicantsToRows = (
  coApplicantJson: string | undefined | null,
): CoResidentFormRow[] => {
  if (!coApplicantJson) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(coApplicantJson);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.map((entry) => {
    const r = (entry ?? {}) as Record<string, unknown>;
    return {
      fk_user_id:
        r.fk_user_id !== undefined && r.fk_user_id !== null
          ? String(r.fk_user_id)
          : undefined,
      first_name: trim(r.first_name),
      middle_name: trim(r.middle_name),
      last_name: trim(r.last_name),
      invite_code: trim(r.applicant_invite_code ?? r.invite_code),
      phone: formatPhoneNumber(
        String(r.mobile_no ?? r.phone ?? "").replace(/\D/g, "").slice(0, 15),
      ),
      email: trim(r.email).toLowerCase(),
      spouse: r.spouse === true || r.spouse === 1 || r.spouse === "1",
      relationship: trim(r.relationship_type ?? r.relationship),
      applicant_status: trim(r.applicant_status) || "NOT_STARTED",
    };
  });
};

export const shouldConfirmRentAmount = (value: unknown): boolean => {
  const rent = parseFloat(String(value ?? "").replace(/[^0-9.]+/g, ""));
  if (Number.isNaN(rent)) return false;
  return rent < 500 || rent > 2500;
};

/** Angular `maskformatPhoneNumber` — (XXX) XXX-XXXX for leasing consultant / co-resident phones. */
export const formatAgentPhoneValue = (raw: unknown): string => {
  const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 15);
  if (!digits) return "";
  return formatPhoneNumber(digits);
};

/** Saved application property id (Angular `applicationData.property.property_id`). */
export const getSavedPropertyId = (allData: ApplicationDetailData): string => {
  const id = allData?.property?.property_id ?? allData?.fk_property_id;
  return id != null && String(id).trim() !== "" ? String(id) : "";
};

export const formatAgentOptionLabel = (agent: PropertyAgentRow): string => {
  const fromName = trim(agent?.agent_name);
  if (fromName) return fromName;
  const fromParts = [agent?.first_name, agent?.last_name]
    .map((p) => trim(p))
    .filter(Boolean)
    .join(" ");
  return fromParts || String(agent?.agent_id ?? "");
};

/** Build agent dropdown + lookup map (keys: agent_id and user_id when present). */
export const buildAgentOptionsFromRows = (
  rows: PropertyAgentRow[],
  applicationAllData: ApplicationDetailData,
  currentAgentId: string,
): { byId: Record<string, PropertyAgentRow>; opts: SearchableSelectOption[] } => {
  const byId: Record<string, PropertyAgentRow> = {};
  const opts: SearchableSelectOption[] = [];

  for (const a of rows) {
    const id = a?.agent_id != null ? String(a.agent_id) : "";
    if (!id) continue;
    byId[id] = a;
    const userId = a?.user_id != null ? String(a.user_id) : "";
    if (userId && userId !== id) byId[userId] = a;
    opts.push({ value: id, label: formatAgentOptionLabel(a), data: a });
  }

  opts.push({
    value: "Other",
    label: "Other",
    data: { agent_id: "Other", agent_name: "Other" },
  });

  const currentId = trim(currentAgentId);
  if (
    currentId &&
    currentId !== "Other" &&
    !opts.some((o) => String(o.value) === currentId)
  ) {
    const agentRow = applicationAllData?.agent ?? {};
    opts.unshift({
      value: currentId,
      label: formatAgentOptionLabel(agentRow) || currentId,
      data: agentRow,
    });
    if (!byId[currentId]) byId[currentId] = agentRow;
  }

  return { byId, opts };
};

/** Angular `addRemoveAgentForm(1)` when selected property matches saved property. */
export const getAgentPrefillForSavedProperty = (
  allData: ApplicationDetailData,
): {
  agent_id: string;
  agent_name: string;
  agent_email: string;
  agent_phone: string;
} | null => {
  if (!allData) return null;
  const hasRealAgentId =
    allData?.agent?.user_id != null &&
    String(allData.agent.user_id).trim() !== "";

  if (!hasRealAgentId && allData?.other_agent) {
    const other = allData.other_agent;
    return {
      agent_id: "Other",
      agent_name: String(other?.agent_name ?? ""),
      agent_email: String(other?.agent_email ?? ""),
      agent_phone: formatAgentPhoneValue(other?.agent_mobile_no),
    };
  }

  if (allData?.agent) {
    const agentId = String(
      allData.agent.user_id ??
        allData.fk_agent_id ??
        allData.agent.agent_id ??
        "",
    );
    if (!agentId) return null;
    const name =
      allData.agent.first_name && allData.agent.last_name
        ? `${allData.agent.first_name} ${allData.agent.last_name}`
        : String(allData.agent.agent_name ?? "");
    return {
      agent_id: agentId,
      agent_name: name,
      agent_email: String(allData.agent.email ?? allData.agent.agent_email ?? ""),
      agent_phone: formatAgentPhoneValue(
        allData.agent.mobile_no ?? allData.agent.agent_mobile_no,
      ),
    };
  }

  return null;
};

/** Angular `getAgents`: when the API returns exactly one consultant, auto-fill selection + contact fields. */
export const getSingleAgentAutofill = (
  rows: PropertyAgentRow[],
): {
  agent_id: string;
  agent_email: string;
  agent_phone: string;
} | null => {
  if (rows.length !== 1) return null;
  const only = rows[0];
  const onlyId = only?.agent_id != null ? String(only.agent_id) : "";
  if (!onlyId) return null;
  return {
    agent_id: onlyId,
    agent_email: String(only?.email ?? ""),
    agent_phone: formatAgentPhoneValue(only?.mobile_no),
  };
};

/** Auto-fill when one consultant is returned unless saved property prefill or "Other" is selected. */
export const shouldApplySingleAgentAutofill = (
  prevAgentId: string,
  prefill: ReturnType<typeof getAgentPrefillForSavedProperty>,
): boolean => {
  if (prefill) return false;
  if (trim(prevAgentId) === "Other") return false;
  return true;
};

/** View-mode leasing consultant fields (Angular resident Property.tsx parity). */
export const getViewLeasingConsultantFields = (
  allData: ApplicationDetailData,
  propertyRecord: PropertyTabPropertyRecord | null | undefined,
) => {
  const agentData = allData?.agent ?? {};
  const otherAgentData = allData?.other_agent ?? {};
  const propertyData = propertyRecord ?? allData?.property ?? {};

  let name = "";
  if (agentData.first_name || agentData.last_name) {
    name = [agentData.first_name, agentData.last_name].filter(Boolean).join(" ");
  } else if (otherAgentData.agent_name) {
    name = String(otherAgentData.agent_name);
  } else if (agentData.agent_name) {
    name = String(agentData.agent_name);
  }

  const email =
    trim(propertyData.email) ||
    trim(agentData.email) ||
    trim(agentData.agent_email) ||
    trim(otherAgentData.agent_email) ||
    "";

  const phone =
    trim(propertyData.mobile_no) ||
    trim(agentData.mobile_no) ||
    trim(agentData.agent_mobile_no) ||
    trim(otherAgentData.agent_mobile_no) ||
    "";

  return { name, email, phone };
};
